import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  Users, 
  Shield,
  UserCheck,
  Package,
  Wrench,
  CheckCircle,
  XCircle,
  Phone,
  Mail,
  MapPin,
  Download,
  RefreshCw
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    nom: '', 
    email: '', 
    password: '', 
    role: 'client',
    phone: '', 
    ville: ''
  });
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/users`, { headers });
      
      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Erreur chargement');
      }

      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      if (editingUser) {
        const response = await fetch(`${API_URL}/users/${editingUser.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(form)
        });

        if (!response.ok) throw new Error('Erreur mise à jour');
        setMessage('Utilisateur mis à jour avec succès');
      } else {
        const response = await fetch(`${API_URL}/users`, {
          method: 'POST',
          headers,
          body: JSON.stringify(form)
        });

        if (!response.ok) throw new Error('Erreur création');
        setMessage('Utilisateur créé avec succès');
      }

      setForm({ nom: '', email: '', password: '', role: 'client', phone: '', ville: '' });
      setShowCreate(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setMessage('Erreur lors de l\'opération');
      console.error('Erreur:', err);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      nom: user.nom,
      email: user.email,
      password: '',
      role: user.role,
      phone: user.phone || '',
      ville: user.ville || ''
    });
    setShowCreate(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) throw new Error('Erreur suppression');
      
      fetchUsers();
      setMessage('Utilisateur supprimé avec succès');
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const getRoleConfig = (role) => {
    const configs = {
      admin: { 
        label: 'Administrateur', 
        bg: 'bg-red-50', 
        text: 'text-red-700', 
        border: 'border-red-200',
        icon: <Shield className="w-3 h-3" />
      },
      chef_equipe: { 
        label: 'Chef d\'équipe', 
        bg: 'bg-purple-50', 
        text: 'text-purple-700', 
        border: 'border-purple-200',
        icon: <UserCheck className="w-3 h-3" />
      },
      client: { 
        label: 'Client', 
        bg: 'bg-blue-50', 
        text: 'text-blue-700', 
        border: 'border-blue-200',
        icon: <Users className="w-3 h-3" />
      },
      validateur: { 
        label: 'Validateur', 
        bg: 'bg-green-50', 
        text: 'text-green-700', 
        border: 'border-green-200',
        icon: <CheckCircle className="w-3 h-3" />
      },
      magasinier: { 
        label: 'Magasinier', 
        bg: 'bg-yellow-50', 
        text: 'text-yellow-700', 
        border: 'border-yellow-200',
        icon: <Package className="w-3 h-3" />
      },
      mecanicien: { 
        label: 'Mécanicien', 
        bg: 'bg-indigo-50', 
        text: 'text-indigo-700', 
        border: 'border-indigo-200',
        icon: <Wrench className="w-3 h-3" />
      }
    };
    return configs[role] || { label: role, bg: 'bg-gray-50', text: 'text-gray-700', icon: <Users className="w-3 h-3" /> };
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const roleStats = {
    total: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    chef_equipe: users.filter(u => u.role === 'chef_equipe').length,
    client: users.filter(u => u.role === 'client').length,
    validateur: users.filter(u => u.role === 'validateur').length,
    magasinier: users.filter(u => u.role === 'magasinier').length,
    mecanicien: users.filter(u => u.role === 'mecanicien').length,
  };

  const handleExport = () => {
    const csvContent = [
      ['Nom', 'Email', 'Rôle', 'Téléphone', 'Ville', 'Date de création'],
      ...filteredUsers.map(user => [
        user.nom,
        user.email,
        user.role,
        user.phone || '',
        user.ville || '',
        new Date(user.created_at).toLocaleDateString('fr-FR')
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
            <p className="text-gray-600 mt-1">Administration des comptes et permissions</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </button>
            <button
              onClick={fetchUsers}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </button>
            <button
              onClick={() => { 
                setShowCreate(true); 
                setEditingUser(null); 
                setForm({ nom: '', email: '', password: '', role: 'client', phone: '', ville: '' }); 
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message.includes('Erreur') 
              ? 'bg-red-50 border border-red-200 text-red-700' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {message.includes('Erreur') ? (
              <XCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            )}
            <span className="font-medium">{message}</span>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistiques par rôle</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {Object.entries(roleStats).map(([role, count]) => {
            const config = getRoleConfig(role);
            return (
              <div 
                key={role} 
                className={`${config.bg} ${config.border} border rounded-xl p-4 hover:shadow-sm transition-shadow cursor-pointer ${
                  roleFilter === role ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                }`}
                onClick={() => setRoleFilter(role === 'total' ? 'all' : role)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`${config.text} font-semibold text-2xl`}>{count}</div>
                  <div className={`${config.text}`}>
                    {config.icon}
                  </div>
                </div>
                <div className={`${config.text} text-sm font-medium capitalize`}>
                  {role === 'total' ? 'Total' : config.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filtres */}
      <div className="mb-8 bg-white border rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateur</option>
              <option value="chef_equipe">Chef d'équipe</option>
              <option value="client">Client</option>
              <option value="validateur">Validateur</option>
              <option value="magasinier">Magasinier</option>
              <option value="mecanicien">Mécanicien</option>
            </select>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      {showCreate && (
        <div className="mb-8 bg-white border rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingUser ? 'Modifier l\'utilisateur' : 'Créer un nouvel utilisateur'}
            </h2>
            <button
              onClick={() => { setShowCreate(false); setEditingUser(null); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input 
                  placeholder="John Doe" 
                  value={form.nom} 
                  onChange={e => setForm({ ...form, nom: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input 
                  placeholder="john@example.com" 
                  type="email" 
                  value={form.email} 
                  onChange={e => setForm({ ...form, email: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {editingUser ? 'Nouveau mot de passe' : 'Mot de passe *'}
                </label>
                <input 
                  placeholder="••••••••" 
                  type="password" 
                  value={form.password} 
                  onChange={e => setForm({ ...form, password: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  required={!editingUser}
                  minLength="6"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle *
                </label>
                <select 
                  value={form.role} 
                  onChange={e => setForm({ ...form, role: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                >
                  <option value="client">Client</option>
                  <option value="admin">Administrateur</option>
                  <option value="chef_equipe">Chef d'équipe</option>
                  <option value="validateur">Validateur</option>
                  <option value="magasinier">Magasinier</option>
                  <option value="mecanicien">Mécanicien</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input 
                  placeholder="06 XX XX XX XX" 
                  value={form.phone} 
                  onChange={e => setForm({ ...form, phone: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ville
                </label>
                <input 
                  placeholder="Casablanca" 
                  value={form.ville} 
                  onChange={e => setForm({ ...form, ville: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4 border-t">
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
              >
                {editingUser ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mettre à jour
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer l'utilisateur
                  </>
                )}
              </button>
              <button 
                type="button" 
                onClick={() => { setShowCreate(false); setEditingUser(null); }} 
                className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Localisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-16 text-center">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Users className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {searchTerm || roleFilter !== 'all' ? 'Aucun résultat trouvé' : 'Aucun utilisateur'}
                    </h3>
                    <p className="text-gray-500 mb-4 max-w-sm mx-auto">
                      {searchTerm || roleFilter !== 'all' 
                        ? 'Essayez de modifier vos critères de recherche' 
                        : 'Commencez par créer votre premier utilisateur'}
                    </p>
                    {(searchTerm || roleFilter !== 'all') ? (
                      <button
                        onClick={() => { setSearchTerm(''); setRoleFilter('all'); }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Réinitialiser les filtres
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowCreate(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Créer un utilisateur
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => {
                  const roleConfig = getRoleConfig(user.role);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{user.nom}</div>
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <Mail className="w-3 h-3 mr-2" />
                            {user.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${roleConfig.bg} ${roleConfig.border} border`}>
                          <span className={`${roleConfig.text} mr-2`}>
                            {roleConfig.icon}
                          </span>
                          <span className={`${roleConfig.text} text-xs font-medium`}>
                            {roleConfig.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {user.phone ? (
                          <div className="flex items-center text-sm text-gray-900">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {user.phone}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Non renseigné</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.ville ? (
                          <div className="flex items-center text-sm text-gray-900">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            {user.ville}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Non renseignée</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Affichage de <span className="font-semibold">{filteredUsers.length}</span> utilisateur{filteredUsers.length > 1 ? 's' : ''}
                {searchTerm || roleFilter !== 'all' ? ' (filtrés)' : ''}
              </div>
              <div className="text-sm text-gray-500">
                Total : <span className="font-medium">{roleStats.total}</span> utilisateurs
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}